import { useDispatch } from 'react-redux'
import { useEffect, useLayoutEffect, useState } from 'react'
import { fetchProductsFiltered, fetchPreviousProductsFiltered } from 'reducers/productReducer'
import useTypedSelector from 'interfaces/useTypedSelector'
import { useActiveCompany } from 'hooks/useActiveCompany/useActiveCompany'
import { ActiveFilters } from 'reducers/filtersReducer'
import { allergenFilterList } from 'components/filteredProductsList/allergenFilterList'
import { AllergenCode } from '@local-types/products/codes'
import products from 'apiService/products'
import { getActiveMainTreeFilter, getActiveMustIncludeTreeFilters } from 'components/filteredProductsList/helpers'
import { StorefrontCategoryTreeItem } from '@local-types/categoryTrees/storefrontCategoryTree'
import StorefrontFilters from '@local-types/filters/storefrontFilters'
import { useRememberScrollPosition } from 'hooks/useRememberScrollPosition/useRememberScrollPosition'

const getFlat = (item: StorefrontCategoryTreeItem): string[] => {
	const { categoryId, children } = item
	return children.reduce((r, o) => [...r, ...getFlat(o)], [categoryId])
}

const arrangeProductQuery = (
	page: number,
	activeFilters: ActiveFilters,
	filters: StorefrontFilters | null | undefined
): products.ProductQuery => {
	// Mapping all allergen filter ids to their values
	const allergenCodesFreeFrom: AllergenCode[] = []
	activeFilters.allergenFilterIds.map(id => {
		// find allergen filter from id or slug
		const allergenFilter = allergenFilterList.find(filter => filter.id === id || filter.slug === id)
		if (allergenFilter)
			allergenFilter.value.map(val => {
				allergenCodesFreeFrom.push(val)
			})
	})

	// Mapping the currently selected category in the main tree filter to its id and it's children's ids
	// Getting the id at the end of the path
	const mainTreeFilter = getActiveMainTreeFilter(activeFilters.catTreePath, filters?.mainCategoryTree)
	// Flattening out every node id
	const ids =
		!mainTreeFilter.isRoot && mainTreeFilter.item ? getFlat(mainTreeFilter.item as StorefrontCategoryTreeItem) : []

	const catTreeFilter = getActiveMustIncludeTreeFilters(
		activeFilters.catTreeFilterIds,
		filters?.categoryFilterTrees || []
	)
	const mustIncludeCategoryIds = catTreeFilter ? getFlat(catTreeFilter) : []

	return {
		page,
		categoryIds: ids,
		mustIncludeCategoryIds: mustIncludeCategoryIds,
		allergenCodesFreeFrom,
		search: activeFilters.search,
		sort: activeFilters.sort,
	}
}

const useProductsFiltered = () => {
	const dispatch = useDispatch()
	const { authState } = useTypedSelector(({ auth }) => auth)
	const { activeRelation } = useActiveCompany()
	const { filteredProductList } = useTypedSelector(({ products }) => products)
	const { page, products, status, totalRecords } = filteredProductList
	const { activeFilters, filters, urlParamsStatus } = useTypedSelector(({ filters }) => filters)
	const [hasLoaded, setHasLoaded] = useState(false)
	const PAGE_SIZE = 12

	useRememberScrollPosition()

	const fetchPreviousPage = () => {
		if (filters === null) return

		let previousPage = 1
		if (page > 1) {
			const pagesInView = Math.ceil(products.length / PAGE_SIZE)
			previousPage = page - pagesInView
		}

		dispatch(
			fetchPreviousProductsFiltered(
				arrangeProductQuery(previousPage, activeFilters, filters),
				activeRelation?.company.id
			)
		)
	}

	const fetchNextPage = () => {
		if (filters === null) return
		dispatch(fetchProductsFiltered(arrangeProductQuery(page + 1, activeFilters, filters), activeRelation?.company.id))
	}

	// Initial load
	useLayoutEffect(() => {
		if (filters === null || urlParamsStatus === 'not-set' || authState === 'pending' || hasLoaded) return
		dispatch(fetchProductsFiltered(arrangeProductQuery(page, activeFilters, filters), activeRelation?.company.id))
		setHasLoaded(true)
	}, [filters, urlParamsStatus, authState])

	// User has switched active relations or updated the filters
	useEffect(() => {
		if (filters === null || !hasLoaded) return
		if (status === 'fulfilled') {
			dispatch(fetchProductsFiltered(arrangeProductQuery(1, activeFilters, filters), activeRelation?.company.id))
		}
	}, [activeRelation, activeFilters, filters])

	return {
		products,
		loading: status === 'pending',
		lazyLoading: status === 'additional-page-pending',
		status,
		error: status === 'failed',
		totalRecords: totalRecords,
		pageSize: PAGE_SIZE,
		page,
		fetchNextPage,
		fetchPreviousPage,
	}
}

export default useProductsFiltered
